import { zodResolver } from "@hookform/resolvers/zod";
import InputField from "components/fields/InputField";
import {
  OwnerEmailFormFieldsName,
  OwnerOtpFormFieldsName,
  ownerEmailFormDefaultValues,
  ownerEmailFormSchema,
  ownerOtpFormDefaultValues,
  ownerOtpFormSchema,
} from "constants/react-hook-form";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { FaRegTrashAlt } from "react-icons/fa";
import { toast } from "react-toastify";
import { _removeBotOwner, _requestBotOwnerOtp, _verifyBotOwner } from "Services/api";
import { getVendorToken } from "utils/session";

const toOwnerLinkList = (value) => (Array.isArray(value) ? value.filter(Boolean) : []);

const extractCode = (payload) => {
  if (!payload || typeof payload !== "object") return undefined;
  const message = payload.message;
  if (message && typeof message === "object" && message.code) return message.code;
  return payload.data?.code || payload.code;
};

const extractMessage = (payload, fallback) => {
  const message = payload?.message;
  if (typeof message === "string" && message) return message;
  if (message && typeof message === "object" && message.message) return message.message;
  return fallback;
};

const extractAttempts = (payload) => {
  const message = payload?.message;
  if (message && typeof message === "object" && message.attempts_remaining !== undefined) {
    return message.attempts_remaining;
  }
  return payload?.data?.attempts_remaining;
};

const formatCountdown = (seconds) => {
  const safeSeconds = Math.max(0, Number(seconds || 0));
  const minutes = Math.floor(safeSeconds / 60);
  const remainingSeconds = String(safeSeconds % 60).padStart(2, "0");
  return `${minutes}:${remainingSeconds}`;
};

const OwnerEmailLinkForm = ({ botId, ownerLinks, onOwnersChange }) => {
  const [step, setStep] = useState("email");
  const [submittedEmail, setSubmittedEmail] = useState("");
  const [otpExpiresIn, setOtpExpiresIn] = useState(0);
  const [ownerRecordCount, setOwnerRecordCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [removingKey, setRemovingKey] = useState("");
  const [message, setMessage] = useState(null);
  const [linkedOwnerLinks, setLinkedOwnerLinks] = useState(() => toOwnerLinkList(ownerLinks));

  const {
    control: emailControl,
    handleSubmit: handleEmailSubmit,
    reset: resetEmailForm,
  } = useForm({
    resolver: zodResolver(ownerEmailFormSchema),
    defaultValues: ownerEmailFormDefaultValues,
  });

  const {
    control: otpControl,
    handleSubmit: handleOtpSubmit,
    reset: resetOtpForm,
  } = useForm({
    resolver: zodResolver(ownerOtpFormSchema),
    defaultValues: ownerOtpFormDefaultValues,
  });

  useEffect(() => {
    setLinkedOwnerLinks(toOwnerLinkList(ownerLinks));
  }, [ownerLinks]);

  useEffect(() => {
    if (step !== "otp" || !otpExpiresIn) return undefined;
    const timer = window.setInterval(() => {
      setOtpExpiresIn((value) => Math.max(0, value - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [otpExpiresIn, step]);

  const linkedRows = useMemo(
    () =>
      linkedOwnerLinks.map((owner, index) => ({
        email: owner.email,
        ownerId: owner.owner_id,
        hotelName: owner.hotel_name,
        verifiedAt: owner.verified_at,
        key: owner.email || String(owner.owner_id || index),
      })),
    [linkedOwnerLinks]
  );

  const syncOwnersUp = (bot) => {
    if (!bot) return;
    const links = toOwnerLinkList(bot.owner_links);
    setLinkedOwnerLinks(links);
    onOwnersChange?.({ owner_links: links });
  };

  const resetVerificationState = () => {
    setStep("email");
    setSubmittedEmail("");
    setOtpExpiresIn(0);
    setOwnerRecordCount(0);
    resetOtpForm(ownerOtpFormDefaultValues);
  };

  const reportFailure = (payload, defaultText) => {
    const code = extractCode(payload);
    const attempts = extractAttempts(payload);

    switch (code) {
      case "OWNER_EMAIL_REQUIRED":
        setMessage({ type: "error", text: "Enter an owner email address to continue." });
        return "email";
      case "OWNER_HOTELS_NOT_FOUND":
        setMessage({
          type: "error",
          text: extractMessage(payload, "This email is not linked to a valid hotel owner."),
        });
        return "email";
      case "OWNER_EMAIL_NOT_FOUND":
        setMessage({ type: "error", text: "No account was found for this email address." });
        return "email";
      case "OWNER_EMAIL_OTP_INVALID":
        setMessage({
          type: "error",
          text:
            attempts !== undefined && attempts !== null
              ? `The code is invalid. ${attempts} attempt${attempts === 1 ? "" : "s"} remaining.`
              : "The code is invalid.",
        });
        return "otp";
      case "OWNER_EMAIL_OTP_EXPIRED":
        resetOtpForm(ownerOtpFormDefaultValues);
        setOtpExpiresIn(0);
        setMessage({
          type: "error",
          text: "That code has expired. Request a fresh code to continue.",
        });
        return "email";
      case "OWNER_EMAIL_OTP_ATTEMPTS_EXCEEDED":
        resetOtpForm(ownerOtpFormDefaultValues);
        setOtpExpiresIn(0);
        setMessage({
          type: "error",
          text: "Too many incorrect attempts. Request a fresh code to continue.",
        });
        return "email";
      case "OWNER_EMAIL_OTP_UNAVAILABLE":
        setMessage({
          type: "error",
          text: "Owner verification is temporarily unavailable. Please try again shortly.",
        });
        return "email";
      case "BOT_OWNERS_UPDATE_FAILED":
        setMessage({
          type: "error",
          text: "Could not save the bot owner. Please try again.",
        });
        return step;
      default: {
        const text = extractMessage(payload, defaultText);
        setMessage({ type: "error", text });
        toast.error(text);
        return step;
      }
    }
  };

  const handleRequestOtp = async ({ [OwnerEmailFormFieldsName.EMAIL]: email }) => {
    const accessToken = getVendorToken();
    const normalizedEmail = email.trim();
    try {
      setLoading(true);
      setMessage(null);
      const response = await _requestBotOwnerOtp({ botId, email: normalizedEmail, accessToken });

      if (response?.status) {
        const expiresIn = Number(response?.data?.expires_in || 0);
        const ownerCount = Number(response?.data?.owner_links_count || 0);
        setSubmittedEmail(normalizedEmail);
        setOtpExpiresIn(expiresIn);
        setOwnerRecordCount(ownerCount);
        resetOtpForm(ownerOtpFormDefaultValues);
        setStep("otp");
        setMessage({
          type: "success",
          text: `Verification code sent to ${normalizedEmail}.${
            ownerCount ? ` ${ownerCount} owner record${ownerCount === 1 ? "" : "s"} found.` : ""
          }`,
        });
        return;
      }

      setStep(reportFailure(response, "Could not send the verification code."));
    } catch (error) {
      setStep(reportFailure(error?.response?.data, "Could not send the verification code."));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async ({ [OwnerOtpFormFieldsName.OTP]: otp }) => {
    const accessToken = getVendorToken();
    try {
      setLoading(true);
      setMessage(null);
      const response = await _verifyBotOwner({
        botId,
        email: submittedEmail,
        otp: otp.trim(),
        accessToken,
      });

      if (response?.status) {
        syncOwnersUp(response?.data?.bot);
        resetEmailForm(ownerEmailFormDefaultValues);
        resetVerificationState();
        setMessage({ type: "success", text: `${submittedEmail} linked successfully.` });
        return;
      }

      setStep(reportFailure(response, "Could not verify the code."));
    } catch (error) {
      setStep(reportFailure(error?.response?.data, "Could not verify the code."));
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!submittedEmail) return;
    await handleRequestOtp({ [OwnerEmailFormFieldsName.EMAIL]: submittedEmail });
  };

  const handleChangeEmail = () => {
    resetVerificationState();
    setMessage(null);
  };

  const handleRemoveOwner = async ({ email, ownerId, key }) => {
    const accessToken = getVendorToken();

    // Snapshot for rollback if the request fails.
    const prevLinks = linkedOwnerLinks;

    // Compute the post-removal list and apply it immediately, so a single
    // click updates the UI without waiting on the server echoing back the bot.
    const nextLinks = prevLinks.filter((owner) => {
      if (email && owner.email === email) return false;
      if (ownerId !== undefined && ownerId !== null && String(owner.owner_id) === String(ownerId)) {
        return false;
      }
      return true;
    });

    try {
      setRemovingKey(key);
      setMessage(null);
      setLinkedOwnerLinks(nextLinks);

      const response = await _removeBotOwner({ botId, email, ownerId, accessToken });

      if (response?.status) {
        // Trust the server's bot only if it actually reflects the removal;
        // some responses echo a pre-commit snapshot that still lists the owner,
        // which would otherwise re-add the row and force a second click.
        const bot = response?.data?.bot;
        const serverLinks = toOwnerLinkList(bot?.owner_links);
        const stillListed = serverLinks.some(
          (owner) =>
            (!!email && owner.email === email) ||
            (ownerId !== undefined &&
              ownerId !== null &&
              String(owner.owner_id) === String(ownerId))
        );

        if (bot && !stillListed) {
          syncOwnersUp(bot);
        } else {
          onOwnersChange?.({ owner_links: nextLinks });
        }
        setMessage({ type: "success", text: `${email || ownerId} removed.` });
        return;
      }

      // Logical failure — restore the row.
      setLinkedOwnerLinks(prevLinks);
      reportFailure(response, "Could not remove this owner.");
    } catch (error) {
      // Network/server error — restore the row.
      setLinkedOwnerLinks(prevLinks);
      reportFailure(error?.response?.data, "Could not remove this owner.");
    } finally {
      setRemovingKey("");
    }
  };

  if (!botId) return null;

  const buttonBase =
    "border-transparent inline-flex items-center justify-center whitespace-nowrap rounded-md border px-4 py-2 font-semibold text-white focus:outline-none focus:ring-2 focus:ring-offset-2";
  const buttonEnabled = "bg-[#007bff] hover:bg-[#007bff] focus:ring-[#007bff]";
  const buttonDisabled = "cursor-not-allowed bg-gray-400";

  return (
    <div className="mt-4">
      <label className="text-black-800 mb-1 block font-bold">Bot Owners</label>
      <p className="mb-2 text-sm text-gray-500">
        Link a hotel owner by verifying their email with a one-time code.
      </p>

      {message && (
        <div
          className={`mb-3 rounded-md p-2 text-sm ${
            message.type === "success"
              ? "bg-green-100 text-green-800"
              : message.type === "info"
              ? "bg-blue-100 text-blue-800"
              : "bg-red-100 text-red-800"
          }`}
        >
          {message.text}
        </div>
      )}

      {step === "email" ? (
        <form onSubmit={handleEmailSubmit(handleRequestOtp)} noValidate>
          <div className="flex items-start gap-2">
            <div className="w-full">
              <InputField
                control={emailControl}
                name={OwnerEmailFormFieldsName.EMAIL}
                type="email"
                className="mt-0 h-auto w-full rounded border p-2"
                placeholder="owner@example.com"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className={`${buttonBase} ${loading ? buttonDisabled : buttonEnabled}`}
            >
              {loading ? "Sending..." : "Request OTP"}
            </button>
          </div>
        </form>
      ) : (
        <form onSubmit={handleOtpSubmit(handleVerifyOtp)} noValidate>
          <p className="mb-1 text-sm text-gray-600">
            Enter the 6-digit code sent to <b>{submittedEmail}</b>.
            {otpExpiresIn ? (
              <span className="ml-1 text-gray-500">
                Expires in {formatCountdown(otpExpiresIn)}.
              </span>
            ) : null}
            {ownerRecordCount ? (
              <span className="ml-1 text-gray-500">
                {ownerRecordCount} owner record{ownerRecordCount === 1 ? "" : "s"} found.
              </span>
            ) : null}
          </p>
          <div className="flex items-start gap-2">
            <InputField
              control={otpControl}
              name={OwnerOtpFormFieldsName.OTP}
              type="text"
              inputMode="numeric"
              maxLength={6}
              autoComplete="one-time-code"
              className="mt-0 h-auto w-full rounded border p-2 tracking-widest"
              placeholder="123456"
            />
            <button
              type="submit"
              disabled={loading}
              className={`${buttonBase} ${loading ? buttonDisabled : buttonEnabled}`}
            >
              {loading ? "Verifying..." : "Verify & link"}
            </button>
          </div>
          <div className="mt-2 flex gap-4 text-sm">
            <button
              type="button"
              onClick={handleResendCode}
              disabled={loading}
              className="text-[#007bff] hover:underline disabled:cursor-not-allowed disabled:text-gray-400"
            >
              Resend code
            </button>
            <button
              type="button"
              onClick={handleChangeEmail}
              disabled={loading}
              className="text-gray-600 hover:underline disabled:cursor-not-allowed disabled:text-gray-400"
            >
              Change email
            </button>
          </div>
        </form>
      )}

      {linkedRows.length > 0 && (
        <div className="mt-4 overflow-x-auto rounded-md border">
          <table className="min-w-full divide-y text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-3 py-2 font-semibold">Owner email</th>
                <th className="px-3 py-2 font-semibold">Hotel / Business</th>
                <th className="px-3 py-2 font-semibold">Owner ID</th>
                <th className="px-3 py-2 font-semibold">Verified</th>
                <th className="px-3 py-2 font-semibold">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {linkedRows.map((owner) => (
                <tr key={owner.key}>
                  <td className="px-3 py-2">{owner.email || "-"}</td>
                  <td className="px-3 py-2">{owner.hotelName || "-"}</td>
                  <td className="px-3 py-2">{owner.ownerId || "-"}</td>
                  <td className="px-3 py-2">
                    {owner.verifiedAt ? new Date(owner.verifiedAt).toLocaleDateString() : "-"}
                  </td>
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      onClick={() => handleRemoveOwner(owner)}
                      disabled={removingKey === owner.key}
                      className="inline-flex items-center gap-1 text-red-600 hover:text-red-700 disabled:cursor-not-allowed disabled:text-gray-400"
                      aria-label={`Remove ${owner.email || owner.ownerId}`}
                    >
                      <FaRegTrashAlt />
                      {removingKey === owner.key ? "Removing..." : "Remove"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default OwnerEmailLinkForm;
